"use client";

import { c as _c } from "react/compiler-runtime";
import CrossIcon from "@/assets/icons/crossIcon.svg";
import { cn } from "@/lib/utils";
import { mantineScrollAreaProps } from "@/shared/ui/scroll-area/scroll-area";
import { AccordionChevron, Select as MantineSelect, type ComboboxItem, type ComboboxLikeRenderOptionInput, type OptionsFilter } from "@mantine/core";
import Image from "next/image";
import type { ReactNode, RefObject } from "react";
import classes from "./select.module.scss";
const SELECT_CHEVRON_SRC = "/images/roadmap/year-chevron.svg";
export type SelectOption = Readonly<{
  value: string;
  label: string;
  disabled?: boolean;
}>;
export type SelectVariant = "default" | "filter";
export type SelectProps = Readonly<{
  value: string;
  options: ReadonlyArray<SelectOption>;
  onChange: (value: string) => void;
  ariaLabel: string;
  className?: string;
  placeholder?: string;
  onBlur?: () => void;
  disabled?: boolean;
  variant?: SelectVariant;
  clearable?: boolean;
  stretch?: boolean;
  withinPortal?: boolean;
  /** Enables type-to-filter on the select input. */
  searchable?: boolean;
  searchValue?: string;
  onSearchChange?: (value: string) => void;
  nothingFoundMessage?: ReactNode;
  onDropdownClose?: () => void;
  /**
   * Custom options filter. Use with server-side search to return `options`
   * unchanged so Mantine does not re-filter the catalog client-side.
   */
  filter?: OptionsFilter;
  /** Ref to the dropdown ScrollArea viewport (e.g. infinite scroll). */
  viewportRef?: RefObject<HTMLDivElement | null>;
  /** Fired when the dropdown list scrolls (e.g. infinite load-more). */
  onScrollPositionChange?: (position: {
    x: number;
    y: number;
  }) => void;
  renderOption?: (input: ComboboxLikeRenderOptionInput<ComboboxItem>) => ReactNode;
}>;
function SelectChevron() {
  const $ = _c(1);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <Image src={SELECT_CHEVRON_SRC} alt="" width={16} height={16} aria-hidden={true} />;
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  return t0;
}

/**
 * Design-system select (Figma StreamHub UI Kit).
 * Built on Mantine `Select` with styles in `select.module.scss`.
 */
export function Select(t0) {
  const $ = _c(55);
  const {
    value,
    options,
    onChange,
    ariaLabel,
    className,
    placeholder,
    onBlur,
    disabled: t1,
    variant: t2,
    clearable: t3,
    stretch: t4,
    withinPortal: t5,
    searchable: t6,
    searchValue,
    onSearchChange,
    nothingFoundMessage,
    onDropdownClose,
    filter,
    viewportRef,
    onScrollPositionChange,
    renderOption
  } = t0;
  const disabled = t1 === undefined ? false : t1;
  const variant = t2 === undefined ? "default" : t2;
  const clearable = t3 === undefined ? false : t3;
  const stretch = t4 === undefined ? false : t4;
  const withinPortal = t5 === undefined ? true : t5;
  const searchable = t6 === undefined ? false : t6;
  const isFilter = variant === "filter";
  const hasSelection = Boolean(value);
  const showFilterChevron = !hasSelection || !clearable;
  const filterClearSectionMode = hasSelection && clearable ? "clear" : "rightSection";
  let t7;
  if ($[0] !== isFilter) {
    t7 = isFilter ? {
      root: classes.filterRoot,
      wrapper: classes.filterWrapper,
      input: classes.filterInput,
      section: classes.filterSection,
      dropdown: classes.filterDropdown,
      options: classes.filterOptions,
      option: classes.filterOption
    } : {
      root: classes.root,
      wrapper: classes.wrapper,
      input: classes.input,
      dropdown: classes.dropdown,
      options: classes.options,
      option: classes.option
    };
    $[0] = isFilter;
    $[1] = t7;
  } else {
    t7 = $[1];
  }
  const selectClassNames = t7;
  let t8;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = <span className={classes.chevron}><SelectChevron /></span>;
    $[2] = t8;
  } else {
    t8 = $[2];
  }
  let rightSection = t8;
  if (!showFilterChevron) {
    rightSection = undefined;
  } else {
    if (isFilter) {
      let t9;
      if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
        t9 = <AccordionChevron size={16} className={classes.filterChevron} />;
        $[3] = t9;
      } else {
        t9 = $[3];
      }
      rightSection = t9;
    }
  }
  const rightSectionPointerEvents = showFilterChevron ? "none" : "all";
  let t9;
  if ($[4] !== className || $[5] !== isFilter || $[6] !== stretch) {
    t9 = isFilter ? cn(classes.filterField, stretch && classes.filterStretch, className) : cn(classes.field, !className && classes.sized, className);
    $[4] = className;
    $[5] = isFilter;
    $[6] = stretch;
    $[7] = t9;
  } else {
    t9 = $[7];
  }
  let t10;
  if ($[8] !== options) {
    t10 = [...options];
    $[8] = options;
    $[9] = t10;
  } else {
    t10 = $[9];
  }
  const t11 = value || null;
  let t12;
  if ($[10] !== clearable || $[11] !== onChange) {
    t12 = nextValue => {
      if (nextValue !== null) {
        onChange(nextValue);
      } else {
        if (clearable) {
          onChange("");
        }
      }
    };
    $[10] = clearable;
    $[11] = onChange;
    $[12] = t12;
  } else {
    t12 = $[12];
  }
  const t13 = isFilter || clearable ? filterClearSectionMode : undefined;
  let t14;
  if ($[13] !== clearable) {
    t14 = clearable ? {
      "aria-label": "Clear selection",
      className: classes.filterClearButton,
      size: "xs",
      children: <CrossIcon aria-hidden={true} />
    } : undefined;
    $[13] = clearable;
    $[14] = t14;
  } else {
    t14 = $[14];
  }
  const t15 = isFilter ? undefined : false;
  const t16 = rightSection;
  const t17 = isFilter ? undefined : 40;
  let t18;
  if ($[15] !== isFilter || $[16] !== withinPortal) {
    t18 = isFilter ? {
      withinPortal
    } : {
      withinPortal,
      zIndex: 500,
      position: "bottom-end",
      offset: 4,
      width: "target"
    };
    $[15] = isFilter;
    $[16] = withinPortal;
    $[17] = t18;
  } else {
    t18 = $[17];
  }
  const t19 = isFilter ? classes.filterScrollAreaContent : classes.scrollAreaContent;
  let t20;
  if ($[18] !== t19) {
    t20 = cn(mantineScrollAreaProps.classNames.content, t19);
    $[18] = t19;
    $[19] = t20;
  } else {
    t20 = $[19];
  }
  let t21;
  if ($[20] !== t20) {
    t21 = {
      ...mantineScrollAreaProps.classNames,
      content: t20
    };
    $[20] = t20;
    $[21] = t21;
  } else {
    t21 = $[21];
  }
  let t22;
  if ($[22] !== onScrollPositionChange || $[23] !== t21 || $[24] !== viewportRef) {
    t22 = {
      ...mantineScrollAreaProps,
      viewportRef,
      onScrollPositionChange,
      classNames: t21
    };
    $[22] = onScrollPositionChange;
    $[23] = t21;
    $[24] = viewportRef;
    $[25] = t22;
  } else {
    t22 = $[25];
  }
  let t23;
  if ($[26] !== ariaLabel || $[27] !== clearable || $[28] !== disabled || $[29] !== filter || $[30] !== isFilter || $[31] !== nothingFoundMessage || $[32] !== onBlur || $[33] !== onDropdownClose || $[34] !== onSearchChange || $[35] !== placeholder || $[36] !== renderOption || $[37] !== rightSection || $[38] !== rightSectionPointerEvents || $[39] !== searchValue || $[40] !== searchable || $[41] !== selectClassNames || $[42] !== t10 || $[43] !== t11 || $[44] !== t12 || $[45] !== t13 || $[46] !== t14 || $[47] !== t15 || $[48] !== t17 || $[49] !== t18 || $[50] !== t22) {
    t23 = <MantineSelect classNames={selectClassNames} data={t10} value={t11} onChange={t12} onBlur={onBlur} disabled={disabled} placeholder={placeholder} allowDeselect={clearable} clearable={clearable} clearSectionMode={t13} clearButtonProps={t14} searchable={searchable} searchValue={searchValue} onSearchChange={onSearchChange} nothingFoundMessage={nothingFoundMessage} onDropdownClose={onDropdownClose} filter={filter} withAlignedLabels={isFilter} withCheckIcon={t15} renderOption={renderOption} rightSection={t16} rightSectionWidth={t17} rightSectionPointerEvents={rightSectionPointerEvents} comboboxProps={t18} scrollAreaProps={t22} aria-label={ariaLabel} />;
    $[26] = ariaLabel;
    $[27] = clearable;
    $[28] = disabled;
    $[29] = filter;
    $[30] = isFilter;
    $[31] = nothingFoundMessage;
    $[32] = onBlur;
    $[33] = onDropdownClose;
    $[34] = onSearchChange;
    $[35] = placeholder;
    $[36] = renderOption;
    $[37] = rightSection;
    $[38] = rightSectionPointerEvents;
    $[39] = searchValue;
    $[40] = searchable;
    $[41] = selectClassNames;
    $[42] = t10;
    $[43] = t11;
    $[44] = t12;
    $[45] = t13;
    $[46] = t14;
    $[47] = t15;
    $[48] = t17;
    $[49] = t18;
    $[50] = t22;
    $[51] = t23;
  } else {
    t23 = $[51];
  }
  let t24;
  if ($[52] !== t23 || $[53] !== t9) {
    t24 = <div className={t9}>{t23}</div>;
    $[52] = t23;
    $[53] = t9;
    $[54] = t24;
  } else {
    t24 = $[54];
  }
  return t24;
}